import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import { UnicodeEntity } from '@mews/enums';
import { AvailabilityBlock, ServiceType, TimeUnitPeriod } from '@mews-commander/data-access';
import { LocalizedEnum, lt, TimeUnitUtils, useDateTimeLocalizationInfo, useTimezone } from '@mews/framework';
import { Table, TableProvider } from '@optimus-web/core';
import { LocalizedUtcTimeUnit, RouteLink } from '@mews-commander/ui';
import { useResolveRoute } from '@mews/routing';
import { RouteName, DataTestId } from '@mews-commander/enums';
import { keyById } from '@mews/fp';
import { BookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { CompanyName, CustomerName } from '../../../components';
import { AvailabilityBlockReportFilterValues } from '../types';
import { navigateInBaseModal } from '../../app/actions/common';
import { getBlockReleaseStrategyView } from '../../availabilityBlock/utils/getBlockReleaseStrategyView';
import { useAvailabilityReportQuery } from '../queries/useAvailabilityReportQuery';
import { useAvailabilityBlockReportContext } from '../tableStateContext';
import { prepareQueryParameters } from '../utils';

interface BlockReleaseStrategyProps {
    availabilityBlock: AvailabilityBlock;
}
const BlockReleaseStrategy = ({ availabilityBlock }: BlockReleaseStrategyProps) => {
    const localizationInfo = useDateTimeLocalizationInfo();

    return getBlockReleaseStrategyView(availabilityBlock, localizationInfo).value;
};

const columnHelper = createColumnHelper<AvailabilityBlock>();
const getTableColumns = (
    openBlockDetail: (availabilityBlockId: string) => void,
    services: Record<string, BookableService>,
    timezone: string
) => [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => (
            <RouteLink
                route={RouteName.AvailabilityBlockDashboard}
                params={{ availabilityBlockId: props.row.original.id }}
                onClick={() => openBlockDetail(props.row.original.id)}
                wordBreak="break-all"
                inherit
                data-test-id={DataTestId.OpenAvailabilityBlockDetail}
                style={{ width: '150px' }}
            >
                {props.getValue()}
            </RouteLink>
        ),
    }),
    columnHelper.accessor('state', {
        header: () => lt('Status'),
        cell: (props) => <LocalizedEnum prefix="AvailabilityBlockState" value={props.getValue()} />,
    }),
    columnHelper.accessor('startUtc', {
        header: () => lt('Arrival'),
        cell: (props) => (
            <LocalizedUtcTimeUnit
                utcString={props.getValue()}
                timeUnit={services[props.row.original.serviceId]?.data?.value?.timeUnitPeriod ?? TimeUnitPeriod.Day}
            />
        ),
        enableSorting: true,
    }),
    columnHelper.accessor('endUtc', {
        header: () => lt('Departure'),
        cell: (props) => {
            const endUtc = props.getValue();
            const service = services[props.row.original.serviceId];

            if (service?.data?.discriminator !== ServiceType.Bookable) {
                return null;
            }

            const { timeUnitPeriod, endOffset } = service.data.value;
            const timeUnitEndUtc = TimeUnitUtils.getNextOverlappingTimeUnitUtc({
                utc: endUtc,
                timeUnit: timeUnitPeriod,
                endOffset,
                timezone,
            });

            return <LocalizedUtcTimeUnit utcString={timeUnitEndUtc} timeUnit={timeUnitPeriod} />;
        },
        enableSorting: true,
    }),
    columnHelper.accessor('releaseStrategy', {
        header: () => lt('ReleaseAt'),
        cell: (props) => <BlockReleaseStrategy availabilityBlock={props.row.original} />,
    }),
    columnHelper.accessor('bookerId', {
        header: () => lt('Booker'),
        cell: (props) => {
            const id = props.getValue();

            if (!id) return UnicodeEntity.Dash;
            return <CustomerName id={id} />;
        },
    }),
    columnHelper.accessor('companyId', {
        header: () => lt('Company'),
        cell: (props) => {
            const id = props.getValue();

            if (!id) return UnicodeEntity.Dash;
            return <CompanyName id={id} />;
        },
    }),
    columnHelper.accessor('travelAgencyId', {
        header: () => lt('TravelAgency'),
        cell: (props) => {
            const id = props.getValue();

            if (!id) return UnicodeEntity.Dash;
            return <CompanyName id={id} />;
        },
    }),
];

interface AvailabilityBlockReportTableProps {
    filters: AvailabilityBlockReportFilterValues;
    services: BookableService[] | undefined;
}

export const AvailabilityBlockReportTable = ({ filters, services }: AvailabilityBlockReportTableProps) => {
    const resolveRoute = useResolveRoute();
    const dispatch = useDispatch();
    const timezone = useTimezone();

    const columns = useMemo(() => {
        const openBlockDetail = (availabilityBlockId: string) =>
            dispatch(navigateInBaseModal(resolveRoute(RouteName.AvailabilityBlockDashboard, { availabilityBlockId })));
        return getTableColumns(openBlockDetail, keyById(services), timezone);
    }, [dispatch, resolveRoute, services, timezone]);

    const { tableState, setTableState } = useAvailabilityBlockReportContext();

    const query = useAvailabilityReportQuery({
        ...prepareQueryParameters(filters, tableState, timezone),
        count: tableState.pagination.pageSize,
        startIndex: tableState.pagination.pageIndex * tableState.pagination.pageSize,
    });

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="section">
            <Table
                uniqueRowId="id"
                data={query.data?.availabilityBlocks ?? []}
                amountOfRows={query.data?.totalRowCount ?? 0}
                loading={query.isFetching}
            />
        </TableProvider>
    );
};
